import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  EntitySlideout,
  saveErrorReason,
  saveErrorToast,
  type BatchAction,
  useBatchSelection,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useClientLocations, useLocationMutations } from "./api";
import { LocationForm } from "./LocationForm/LocationForm";
import { emptyLocationForm } from "./types";
import type { ClientLocation, LocationFormValues } from "./types";

export interface LocationsTabProps {
  clientId: string;
}

const isValidLocation = (v: LocationFormValues): boolean => v.name.trim() !== "";

const toFormValues = (l: ClientLocation): LocationFormValues => ({
  name: l.name,
  street: l.street ?? "",
  street2: l.street2 ?? "",
  city: l.city ?? "",
  state: l.state ?? "",
  postalCode: l.postalCode ?? "",
  country: l.country ?? "",
});

export const LocationsTab = ({ clientId }: LocationsTabProps): JSX.Element => {
  const { rows, isLoading, isFetching, refetch } = useClientLocations(clientId);
  const { create, update, archive } = useLocationMutations(clientId);
  const sel = useBatchSelection([]);

  const [view, setView] = useState<
    { mode: "list" } | { mode: "form"; editing?: ClientLocation }
  >({ mode: "list" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const selectedLocations = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<ClientLocation>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => row.original.name,
    },
    {
      id: "address",
      header: "Address",
      cell: ({ row }) =>
        [row.original.street, row.original.street2].filter(Boolean).join(", "),
    },
    {
      accessorKey: "city",
      header: "City",
      cell: ({ row }) => row.original.city ?? "",
    },
    {
      accessorKey: "state",
      header: "State",
      cell: ({ row }) => row.original.state ?? "",
    },
    {
      accessorKey: "postalCode",
      header: "Postal Code",
      cell: ({ row }) => row.original.postalCode ?? "",
    },
  ];

  const saveLocation = async (values: LocationFormValues) => {
    try {
      if (view.mode === "form" && view.editing) {
        await update(view.editing.id, values);
      } else {
        await create(values);
      }
      setView({ mode: "list" });
      refetch();
    } catch (err) {
      setToast(saveErrorToast(err, "location"));
    }
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    const targets = [...selectedLocations];
    const failures: BatchFailure[] = [];
    for (const c of targets) {
      try {
        await archive(c.id);
      } catch (err) {
        failures.push({ label: c.name, reason: saveErrorReason(err) });
      }
    }
    setToast(
      failures.length === 0
        ? { variant: "success", text: `${targets.length} location(s) archived.` }
        : {
            variant: "danger",
            text: withFailureReasons(
              `Failed on ${failures.length} of ${targets.length} location(s).`,
              failures
            ),
          }
    );
    sel.clear();
    refetch();
  };

  const batchActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Delete",
      onClick: () => setConfirmOpen(true),
      variant: "danger",
    },
  ];

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Locations"
        bare
        isEmpty={rows.length === 0}
        emptyMessage="No locations found."
        onNew={() => setView({ mode: "form" })}
        newButtonLabel="+ Add A Location"
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(locationId) => {
            const row = rows.find((r) => r.id === String(locationId));
            if (row) setView({ mode: "form", editing: row });
          }}
          emptyMessage="No locations found."
        />
      </AdminListCard>

      <EntitySlideout
        isOpen={view.mode === "form"}
        initialValues={
          view.mode === "form" && view.editing
            ? toFormValues(view.editing)
            : emptyLocationForm
        }
        title={
          view.mode === "form" && view.editing
            ? "Edit Location"
            : "Add a Location"
        }
        isValid={isValidLocation}
        onClose={() => setView({ mode: "list" })}
        onSave={saveLocation}
      >
        {(values, onChange) => (
          <LocationForm values={values} onChange={onChange} />
        )}
      </EntitySlideout>

      <ConfirmDeleteModal
        open={confirmOpen}
        title="Archive locations?"
        message={`Are you sure you want to archive ${sel.selectedIds.length} location(s)?`}
        confirmLabel="Archive"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default LocationsTab;
